import React, {useMemo, useState} from 'react';
import {Alert} from 'src/design/atoms/Alert';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {ProjectAssignmentRuleResponse} from 'src/api/ProactApiClient';
import {EntityTypeEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {Spinner} from 'src/design/atoms/Spinner';
import {buildSourceEntityNameLookup} from 'src/app/assignment-rules/assignmentRuleHelpers';
import {useGetDepartmentSelectOptionsQuery} from 'src/redux/queries/department';
import {useGetUserGroupSelectOptionsQuery} from 'src/redux/queries/userGroup';
import {useGetVendorSelectOptionsQuery} from 'src/redux/queries/vendor';
import {useGetProjectAssignmentRulesByProjectQuery} from 'src/redux/queries/projectAssignmentRule';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {AssignmentRuleCard} from './AssignmentRuleCard';
import {AssignmentRuleFormModal} from './AssignmentRuleFormModal';

export interface ProjectAssignmentRulesTabProps {
  projectId: number;
}

// Project membership is owned by ProjectAssignmentRule records with their own
// CRUD, mirroring the Training assignment-rule mechanic.
export const ProjectAssignmentRulesTab: React.FC<
  ProjectAssignmentRulesTabProps
> = ({projectId}) => {
  const projectPerms = useModulePermissions(ModuleEnum.Projects);

  const {
    data: rules,
    isLoading,
    isError,
  } = useGetProjectAssignmentRulesByProjectQuery(projectId);

  const {data: departmentOptions} = useGetDepartmentSelectOptionsQuery({
    start: 0,
    length: 500,
  });
  const {data: groupOptions} = useGetUserGroupSelectOptionsQuery({
    start: 0,
    length: 500,
    active: true,
  });
  const {data: vendorOptions} = useGetVendorSelectOptionsQuery({
    start: 0,
    length: 500,
    active: true,
  });

  const nameLookup = useMemo(
    () =>
      buildSourceEntityNameLookup({
        [EntityTypeEnum.Department]: departmentOptions?.data,
        [EntityTypeEnum.Group]: groupOptions?.data,
        [EntityTypeEnum.Vendor]: vendorOptions?.data,
      }),
    [departmentOptions?.data, groupOptions?.data, vendorOptions?.data]
  );

  const [editingRule, setEditingRule] = useState<
    ProjectAssignmentRuleResponse | undefined
  >(undefined);
  const [creating, setCreating] = useState(false);

  if (isLoading) {
    return <Spinner loadingText="Loading assignment rules..." />;
  }

  if (isError) {
    return (
      <Alert color="red">
        Failed to load assignment rules. Please try again later.
      </Alert>
    );
  }

  const rows = rules ?? [];

  return (
    <Stack gap="md">
      <Alert color="blue" py="xs">
        Assignment rules are live: new matching people are assigned to this
        project automatically (within ~12 hours), and removing someone from the
        audience un-assigns them.
      </Alert>

      {projectPerms.create && (
        <Button
          size="sm"
          variant="outline"
          w="fit-content"
          onClick={() => setCreating(true)}>
          + Add Rule
        </Button>
      )}

      {rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          No assignment rules yet. This project has no members; add a rule to
          start assigning people.
        </Text>
      ) : (
        rows.map(rule => (
          <AssignmentRuleCard
            key={rule.id}
            rule={rule}
            nameLookup={nameLookup}
            canEdit={projectPerms.update}
            onEdit={() => setEditingRule(rule)}
          />
        ))
      )}

      {creating && (
        <AssignmentRuleFormModal
          opened={creating}
          onClose={() => setCreating(false)}
          projectId={projectId}
        />
      )}

      {editingRule && (
        <AssignmentRuleFormModal
          opened={!!editingRule}
          onClose={() => setEditingRule(undefined)}
          projectId={projectId}
          rule={editingRule}
        />
      )}
    </Stack>
  );
};
